import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { ionChevronBackOutline } from '@ng-icons/ionicons';
import { CompanyList } from 'src/app/api/fn/generals/company.model';
import {
  DepartamentService,
  DistrictService,
  ProvinceService,
} from 'src/app/api/services';
import { BusinessLineService } from 'src/app/api/services/business-line.service';
import { CompanyService } from 'src/app/api/services/company.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { ImageUploadModalComponent } from 'src/app/shared/component/image-upload-modal/image-upload-modal.component';
import { InputSelectComponent } from 'src/app/shared/component/input-select/input-select.component';
import { InputTextComponent } from 'src/app/shared/component/input-text/input-text.component';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { BaseFormComponent } from 'src/app/shared/form/base-form.component';
import { CompanyForm } from 'src/app/shared/form/company-form.model';
import { ToastService } from 'src/app/shared/service/toast.service';
import { TokenStorageService } from 'src/app/shared/service/token-storage.service';

@Component({
  selector: 'app-user-company',
  imports: [
    CommonModule,
    LoadingButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    NgIcon,
    ImageUploadModalComponent,
    LoadingButtonComponent,
    InputTextComponent,
    InputSelectComponent,
  ],
  templateUrl: './user-company.component.html',
  viewProviders: [provideIcons({ ionChevronBackOutline })],
  styleUrls: ['./user-company.component.css'],
})
export class UserCompanyComponent extends BaseFormComponent implements OnInit {
  companyForm!: CompanyForm;
  companies: CompanyList[] = [];
  selectedCompanyId: string | null = null;
  showCreateForm: boolean = false;
  departaments!: any[];
  districts!: any[];
  provinces!: any[];
  bussinessLines!: any[];
  isLoading: boolean = true;
  isLoadingForm: boolean = false;

  avatarFile: File | null = null;
  croppedImage: string | null = null;
  showModalUpdate = false;
  modalLoading = false;

  constructor(
    private fb: FormBuilder,
    private departamentService: DepartamentService,
    private provinceService: ProvinceService,
    private districtService: DistrictService,
    private bussinessLineService: BusinessLineService,
    private companyService: CompanyService,
    private tokenStorage: TokenStorageService,
    private router: Router,
    toastService: ToastService
  ) {
    super(toastService);
  }

  ngOnInit(): void {
    this.companyForm = new CompanyForm(this.fb);
    this.companyService.getCompaniesByUserId().subscribe({
      next: (res) => {
        this.companies = res.dto.companys;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
      },
    });

    this.departamentService.departamentoGet().subscribe({
      next: (data) => {
        this.departaments = data.dto.departaments.map((item: any) => ({
          value: item.idDepartament,
          label: item.name,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.DEPARTAMENT.GET_ALL_ERROR]
        );
      },
    });

    this.bussinessLineService.getBusinessLines().subscribe({
      next: (data) => {
        this.bussinessLines = data.dto.businessLines.map((item: any) => ({
          value: item.idBusinessLine,
          label: item.description,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.BUSSINES_LINE.GET_ALL_ERROR]
        );
      },
    });
  }

  selectCompany(id: string) {
    this.selectedCompanyId = id;
  }

  get selectedCompany(): CompanyList | undefined {
    return this.companies.find((c) => c.idCompany === this.selectedCompanyId!);
  }

  triggerFileInput(fileInput: HTMLInputElement) {
    fileInput.click();
  }

  fileChangeEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    if (file.size > 2 * 1024 * 1024) {
      this.toastService.showMessages('error', [
        'El archivo no debe superar los 2MB',
      ]);
      return;
    }

    this.avatarFile = file;
    this.showModalUpdate = true;
  }

  onImageSelected(file: File) {
    if (!file) return;

    this.avatarFile = file;

    const reader = new FileReader();
    reader.onload = () => {
      this.croppedImage = reader.result as string;
    };
    reader.readAsDataURL(file);

    this.showModalUpdate = false;
  }

  onSubmit() {
    if (!this.validateForm(this.companyForm.form)) return;

    const authData = this.tokenStorage.getAuth();
    if (!authData || !authData.user?.idUser) {
      this.toastService.showMessages('error', [MESSAGES.AUTH.FORBIDDEN]);
      return;
    }

    const formValue = this.companyForm.form.value;
    this.isLoadingForm = true;

    const payload: any = {
      idBusinessLine: formValue.businessLine,
      idDistrict: formValue.idDistrict,
      email: formValue.email,
      ruc: formValue.ruc,
      commercialName: formValue.commercialName,
      socialReason: formValue.legalName,
      representatives: [authData.user.idUser],
      urlBannerMiniPage: this.avatarFile,
    };

    this.companyService.createCompany(payload).subscribe({
      next: (res) => {
        this.toastService.showMessages('success', [
          MESSAGES.COMPANY.CREATE_SUCCESS,
        ]);
        this.isLoadingForm = false;
        window.location.reload();
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.COMPANY.CREATE_ERROR]
        );
        this.isLoadingForm = false;
      },
    });
  }

  loadProvinces(idDepartament: string): Promise<void> {
    return new Promise((resolve) => {
      this.provinceService
        .provinciaDepartamentoIdDepartamentGet({ idDepartament })
        .subscribe({
          next: (res) => {
            this.provinces = res?.dto?.provinces.map((item: any) => ({
              value: item.id,
              label: item.name,
            }));
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  loadDistricts(idProvince: string): Promise<void> {
    return new Promise((resolve) => {
      this.districtService
        .distritoProvinciaIdProvinceGet({ idProvince })
        .subscribe({
          next: (res) => {
            this.districts = res?.dto?.districts.map((item: any) => ({
              value: item.idDistrict,
              label: item.name,
            }));
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  handleDepartmentChange(idDepartament: string): void {
    this.companyForm.form
      .get('idProvince')
      ?.setValue(null, { emitEvent: false });
    this.companyForm.form
      .get('idDistrict')
      ?.setValue(null, { emitEvent: false });

    this.provinces = [];
    this.districts = [];

    if (idDepartament) {
      this.companyForm.form.get('idProvince')?.disable();

      this.loadProvinces(idDepartament).then(() => {
        this.companyForm.form.get('idProvince')?.enable();
      });
    } else {
      this.companyForm.form.get('idProvince')?.disable();
      this.companyForm.form.get('idDistrict')?.disable();
    }
  }

  handleProvinceChange(idProvince: string): void {
    this.companyForm.form
      .get('idDistrict')
      ?.setValue(null, { emitEvent: false });
    this.districts = [];

    if (idProvince) {
      this.companyForm.form.get('idDistrict')?.disable();

      this.loadDistricts(idProvince).then(() => {
        this.companyForm.form.get('idDistrict')?.enable();
      });
    } else {
      this.companyForm.form.get('idDistrict')?.disable();
    }
  }

  get f() {
    return this.companyForm.form.controls;
  }

  goToCompany(id: string) {
    this.router.navigate([`/user/company/${id}`]);
  }

  showSection() {
    return this.showCreateForm;
  }
}
